<div class="workspace-page br-compact-page">
  <div class="workspace-page__panel workspace-page__panel--card">
    <header class="workspace-page__heading">
      <h1 class="workspace-page__title">{{ tableName }}</h1>
      <div class="workspace-page__title-actions">
        <button
                type="button"
                class="br-btn br-btn--primary br-btn--sm"
                *ngIf="canWrite && createFn"
                (click)="openAddDialog()">
          <br-icon>add</br-icon> Add
        </button>
      </div>
    </header>

  <!-- Table toolbar: search / show-inactive. Add stays in the page heading,
       matching the Customers list. -->
  <div class="workspace-page__toolbar">
    <mat-form-field appearance="outline" class="filter-field" subscriptSizing="dynamic">
      <br-icon matPrefix>search</br-icon>
      <input matInput placeholder="Filter…" [(ngModel)]="filterText" (ngModelChange)="applySearch()" />
    </mat-form-field>
    <mat-slide-toggle
      [(ngModel)]="showInactive"
      (change)="applyFilter()"
      class="inactive-toggle">
      Show inactive
    </mat-slide-toggle>
  </div>

  <!-- Progress bar while loading or saving -->
  <mat-progress-bar *ngIf="loading || saving" mode="indeterminate" class="config-progress"></mat-progress-bar>

  <!-- Skeleton loading rows -->
  <div *ngIf="loading" class="skeleton-list">
    <div *ngFor="let _ of skeletonRows" class="skeleton-row">
      <div class="skeleton-cell shimmer wide"></div>
      <div class="skeleton-cell shimmer medium"></div>
      <div class="skeleton-cell shimmer narrow"></div>
    </div>
  </div>

  <!-- Data table -->
  <div class="fp-table">
  <mat-table
    *ngIf="!loading"
    [dataSource]="dataSource"
    matSort
    cdkDropList
    [cdkDropListData]="dataSource.data"
    (cdkDropListDropped)="drop($event)"
    class="config-mat-table">

    <!-- Drag-handle column (visible only when reorderFn + canWrite) -->
    <ng-container matColumnDef="drag">
      <th mat-header-cell *matHeaderCellDef class="col-drag"></th>
      <td mat-cell *matCellDef="let row" class="col-drag">
        <br-icon cdkDragHandle class="drag-handle" matTooltip="Drag to reorder">drag_indicator</br-icon>
      </td>
    </ng-container>

    <!-- Dynamic data columns -->
    <ng-container *ngFor="let col of columns" [matColumnDef]="col.field">
      <th mat-header-cell *matHeaderCellDef mat-sort-header [disabled]="col.sortable === false">
        {{ col.label }}
      </th>
      <td mat-cell *matCellDef="let row" [class.inactive-text]="row.isActive === false">

        <!-- View mode -->
        <ng-container *ngIf="editingId !== row.id">
          <s *ngIf="col.field === 'name' && row.isActive === false">{{ row[col.field] }}</s>
          <ng-container *ngIf="!(col.field === 'name' && row.isActive === false)">
            <ng-container *ngIf="col.type === 'boolean'">{{ row[col.field] ? 'Yes' : 'No' }}</ng-container>
            <ng-container *ngIf="col.type === 'select'">{{ getDisplayValue(col, row[col.field]) }}</ng-container>
            <ng-container *ngIf="col.type !== 'boolean' && col.type !== 'select'">{{ row[col.field] }}</ng-container>
          </ng-container>
        </ng-container>

        <!-- Edit mode: non-editable -->
        <ng-container *ngIf="editingId === row.id && col.editable === false">
          {{ row[col.field] }}
        </ng-container>

        <!-- Edit mode: editable -->
        <mat-form-field
          *ngIf="editingId === row.id && col.editable !== false && col.type !== 'boolean'"
          appearance="outline"
          class="cell-edit-field"
          subscriptSizing="dynamic">
          <input
            matInput
            *ngIf="col.type === 'text' || col.type === 'number'"
            [type]="col.type === 'number' ? 'number' : 'text'"
            [(ngModel)]="editData[col.field]"
            [attr.maxlength]="col.maxLength ?? null" />
          <textarea
            matInput
            *ngIf="col.type === 'textarea'"
            [(ngModel)]="editData[col.field]"
            [attr.maxlength]="col.maxLength ?? null"
            rows="2"></textarea>
          <mat-select *ngIf="col.type === 'select'" [(ngModel)]="editData[col.field]">
            <mat-option *ngFor="let opt of col.selectOptions" [value]="opt.value">
              {{ opt.label }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <mat-slide-toggle
          *ngIf="editingId === row.id && col.editable !== false && col.type === 'boolean'"
          [(ngModel)]="editData[col.field]">
        </mat-slide-toggle>

      </td>
    </ng-container>

    <!-- Active indicator column -->
    <ng-container matColumnDef="isActive">
      <th mat-header-cell *matHeaderCellDef>Active</th>
      <td mat-cell *matCellDef="let row">
        <span
          class="status-dot"
          [class.dot-active]="row.isActive !== false"
          [class.dot-inactive]="row.isActive === false"
          [matTooltip]="row.isActive !== false ? 'Active' : 'Inactive'">
        </span>
      </td>
    </ng-container>

    <!-- Actions column -->
    <ng-container matColumnDef="actions">
      <th mat-header-cell *matHeaderCellDef class="col-actions"></th>
      <td mat-cell *matCellDef="let row" class="col-actions">

        <!-- View mode: edit + deactivate -->
        <ng-container *ngIf="editingId !== row.id">
          <br-icon
            *ngIf="row.isSystem"
            matTooltip="System stage cannot be edited, deactivated, or deleted; its order can be changed">
            lock
          </br-icon>
          <button
            mat-icon-button
            matTooltip="Edit"
            *ngIf="updateFn && !row.isSystem"
            [disabled]="editingId != null"
            (click)="startEdit(row)"
            aria-label="Edit row">
            <br-icon>edit</br-icon>
          </button>
          <button
            mat-icon-button
            matTooltip="Deactivate"
            color="warn"
            *ngIf="deactivateFn && row.isActive !== false && !row.isSystem"
            [disabled]="editingId != null"
            (click)="openDeactivateDialog(row)"
            aria-label="Deactivate row">
            <br-icon>remove_circle_outline</br-icon>
          </button>
          <button
            mat-icon-button
            matTooltip="Delete permanently (records using it are updated)"
            color="warn"
            *ngIf="deleteFn && !row.isSystem"
            [disabled]="editingId != null"
            (click)="deleteRow(row)"
            aria-label="Delete row permanently">
            <br-icon>delete_forever</br-icon>
          </button>
        </ng-container>

        <!-- Edit mode: save + cancel -->
        <ng-container *ngIf="editingId === row.id">
          <button
            mat-icon-button
            matTooltip="Save"
            color="primary"
            [disabled]="saving"
            (click)="submitEdit()"
            aria-label="Save">
            <br-icon>check</br-icon>
          </button>
          <button
            mat-icon-button
            matTooltip="Cancel"
            (click)="cancelEdit()"
            aria-label="Cancel">
            <br-icon>close</br-icon>
          </button>
        </ng-container>

      </td>
    </ng-container>

    <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
    <tr
      mat-row
      *matRowDef="let row; columns: displayedColumns;"
      cdkDrag
      [cdkDragData]="row"
      [cdkDragDisabled]="!canWrite || !reorderFn"
      [class.editing-row]="editingId === row.id"
      [class.inactive-row]="row.isActive === false">
    </tr>

    <!-- No-data row -->
    <tr class="mat-row no-data-row" *matNoDataRow>
      <td [attr.colspan]="displayedColumns.length">
        <div class="empty-state" *ngIf="!filterText">
          <br-icon class="empty-icon">list_alt</br-icon>
          <p *ngIf="!showInactive">No active items. Toggle 'Show inactive' to see all.</p>
          <p *ngIf="showInactive">No items yet.</p>
          <button type="button" class="br-btn br-btn--primary br-btn--sm"
                  *ngIf="canWrite && createFn && showInactive" (click)="openAddDialog()">
            <br-icon>add</br-icon> Add
          </button>
        </div>
        <div class="empty-state" *ngIf="filterText">
          <br-icon class="empty-icon">search_off</br-icon>
          <p>No results for "{{ filterText }}".</p>
        </div>
      </td>
    </tr>

  </mat-table>

  <mat-paginator
    *ngIf="!loading"
    [pageSizeOptions]="[10, 25, 50]"
    pageSize="25"
    showFirstLastButtons
    aria-label="Select page">
  </mat-paginator>
  </div>

  </div>
</div>
